import { shallowEqual } from 'react-redux';

import { Box, Label } from '@zillow/constellation-10';

import { useSelector } from '../../../../selectors/useSelector';

import accessUtils from '../../../../utils/accessUtils';

import AcceptZillowApplicationsFieldSet from '../../../../components/WizardModularListing/AcceptZillowApplications/components/AcceptZillowApplicationsFieldSet';
import PetPolicyFormField from '../../../../components/WizardModularListing/PetPolicy/components/PetPolicyFormField';
import ListingTourFormField from '../../../../components/WizardModularListing/ListingTour/components/ListingTourFormField';
import HideAddressFieldSet from '../../../../components/WizardModularListing/HidePropertyAddress/components/HideAddressFieldSet';
import AmenitiesWithAdditionalAmenitiesFields from '../../../../components/WizardModularListing/AmenitiesProperty/components/AmenitiesWithAdditionalAmenitiesFields';
import SquareFootageFormField from '../../../../components/WizardModularListing/PropertyDescription/components/SquareFootageFormField';
import PhotoUploadFormField from '../../../../components/WizardModularListing/AddPhotos/components/PhotoUploadFormField';
import AllowContactByPhoneFieldSet from '../../../../components/WizardModularListing/AllowContactByPhone/components/AllowContactByPhoneFieldSet';
import CostsAndFeesFormField from '../../CostsAndFees/components/CostsAndFeesFormField';
import SFCostAndFeesTotalMonthlyPriceWithFeeAlert from '../../../Alerts/SFCostAndFeesTotalMonthlyPriceWithFeeAlert';
import SFCostAndFeeOptInTotalMonthlyPriceWithFeeAlert from '../../../Alerts/SFCostAndFeeOptInTotalMonthlyPriceWithFeeAlert';

import { WizardBody, WizardFormSectionContainer, WizardHeading } from '../../../../styles/wizard';

const FormSectionContainer = () => {
  const {
    cookieEnabledServices,
    isOptInTotalMonthlyPriceWithFeeEligible,
    isTotalMonthlyPriceWithFeeEligible,
    isUpdateRestricted: isApplicationsUpdateRestricted,
    relist: {
      fieldsToRender: {
        renderSquareFootageField,
        renderAcceptApplicationsField,
        renderPetsField,
        renderPhotosField,
        renderToursField,
        renderAmenitiesField,
        renderHidePropertyAddressField,
        renderAllowContactByPhoneField,
        renderCostsAndFeesField,
      },
    },
  } = useSelector((state) => {
    return {
      cookieEnabledServices: state?.app?.cookieEnabledServices,
      isOptInTotalMonthlyPriceWithFeeEligible:
        state.properties.currentProperty?.totalMonthlyPriceWithFee.isOptInEligible,
      isTotalMonthlyPriceWithFeeEligible: state.properties.currentProperty?.totalMonthlyPriceWithFeeNotice?.isEligible,
      isUpdateRestricted: state.properties.currentProperty.restrictions.applications.isUpdateRestricted,
      relist: state.relist,
    };
  }, shallowEqual);

  const canAccessTotalMonthlyPriceWithFee = accessUtils.canAccessTotalMonthlyPriceWithFee({
    cookieEnabledServices,
    isEligible: isTotalMonthlyPriceWithFeeEligible,
  });

  return (
    <WizardFormSectionContainer data-testid="FormSectionContainer">
      <WizardHeading level={1} textStyle="heading-sm">
        Consider adding additional details
      </WizardHeading>
      <WizardBody>Review and update any information that has changed since you last listed this property.</WizardBody>
      {renderSquareFootageField && (
        <Box css={{ marginTop: { base: '600', lg: '800' }, marginBottom: '100' }}>
          <SquareFootageFormField isRelistingFieldset={true} />
        </Box>
      )}
      {renderPetsField && (
        <Box css={{ marginTop: { base: '600', lg: '800' }, marginBottom: '100' }}>
          <PetPolicyFormField isRelistingFieldset={true} />
        </Box>
      )}
      {renderPhotosField && (
        <Box css={{ marginTop: { base: '600', lg: '800' }, marginBottom: '100' }}>
          <Box css={{ paddingBottom: '100' }}>
            <Label size="lg">Photos</Label>
          </Box>
          <PhotoUploadFormField />
        </Box>
      )}
      {renderToursField && (
        <Box css={{ marginTop: { base: '600', lg: '800' }, marginBottom: '100' }}>
          <Box css={{ paddingBottom: '100' }}>
            <Label size="lg">3D tour</Label>
          </Box>
          <ListingTourFormField isRelistingFieldset={true} />
        </Box>
      )}
      {renderAmenitiesField && (
        <Box css={{ marginTop: { base: '600', lg: '800' }, marginBottom: '100' }}>
          <Box css={{ paddingBottom: '100' }}>
            <Label size="lg">Amenities</Label>
            <Box css={{ marginTop: '100' }} />
            <WizardBody>Sharing more will help renters see themselves in your home.</WizardBody>
          </Box>
          <AmenitiesWithAdditionalAmenitiesFields isRelistingFieldset={true} />
        </Box>
      )}
      {renderHidePropertyAddressField && (
        <Box css={{ marginTop: { base: '600', lg: '800' }, marginBottom: '100' }}>
          <HideAddressFieldSet isRelistingFieldset={true} />
        </Box>
      )}
      {renderAllowContactByPhoneField && (
        <Box css={{ marginTop: { base: '600', lg: '800' }, marginBottom: '100' }}>
          <AllowContactByPhoneFieldSet isRelistingFieldset={true} />
        </Box>
      )}
      {Boolean(isApplicationsUpdateRestricted === false && renderAcceptApplicationsField) && (
        <Box css={{ marginTop: { base: '600', lg: '800' }, marginBottom: '100' }}>
          <AcceptZillowApplicationsFieldSet isRelistingFieldset={true} />
        </Box>
      )}
      {renderCostsAndFeesField && (
        <Box css={{ marginTop: { base: '600', lg: '800' } }}>
          <Label size="lg">What additional costs and fees do you charge?</Label>
          <Box css={{ marginTop: '100', marginBottom: '200' }}>
            <WizardBody>
              {isOptInTotalMonthlyPriceWithFeeEligible ? (
                <>
                  These details help renters know the real cost of renting your property, and helps you meet relevant
                  fee transparency laws.
                </>
              ) : (
                <>
                  We&apos;ve highlighted some of the most common fee{' '}
                  {canAccessTotalMonthlyPriceWithFee ? 'types' : 'categories'}. These details help renters know the real
                  cost of renting your property before they apply.
                </>
              )}
            </WizardBody>
          </Box>

          {canAccessTotalMonthlyPriceWithFee && <SFCostAndFeesTotalMonthlyPriceWithFeeAlert />}
          {isOptInTotalMonthlyPriceWithFeeEligible && <SFCostAndFeeOptInTotalMonthlyPriceWithFeeAlert />}

          <CostsAndFeesFormField displayedLocation="relisting" />
        </Box>
      )}
    </WizardFormSectionContainer>
  );
};

export default FormSectionContainer;
